<template>
  <section class="posters-section" aria-labelledby="posters-heading">
    <div class="posters-header">
      <div
        class="posters-icon"
        v-html="LAPTOP_ICON_SVG"
        aria-hidden="true"
      ></div>
      <div class="posters-heading-row">
        <h2 id="posters-heading" class="posters-title">
          Makercart Add-On: Activity Posters
        </h2>
        <p class="posters-intro">
          Add a classroom-ready activity poster to your Makercart order for $50
          per activity. Each 24" × 36" poster provides a visual reference that
          students and educators can use during the activity.
        </p>
      </div>
    </div>

    <div class="posters-body">
      <div class="posters-gallery">
        <button
          v-for="(poster, index) in posters"
          :key="poster.src"
          class="posters-gallery__item"
          type="button"
          :aria-label="`${poster.alt} (opens image gallery)`"
          @click="openPosterModal(index)"
        >
          <img
            :src="poster.src"
            :alt="poster.alt"
            class="posters-gallery__image"
            width="333"
            height="500"
          />
        </button>
      </div>
      <ActionButton
        variant="primary"
        size="large"
        type="button"
        @click="openPosterModal(0)"
      >
        View Sample Poster
        <template #icon-right>
          <EyeIcon width="24" height="24" color="currentColor" />
        </template>
      </ActionButton>
    </div>
  </section>

  <PosterModal
    v-model="isPosterModalOpen"
    :images="posterImages"
    :initial-index="currentPosterIndex"
    title="Activity Poster"
    @close="closePosterModal"
  />
</template>

<script setup>
import { ref } from "vue";
import ActionButton from "@/components/common/ActionButton.vue";
import PosterModal from "@/components/common/PosterModal.vue";
import { EyeIcon } from "@/components/icons";

const isPosterModalOpen = ref(false);
const currentPosterIndex = ref(0);

const posters = [
  {
    src: "/images/maker-cart/get-started/activity-poster-1.jpg",
    alt: "How to make a scribble bot activity poster",
  },
  {
    src: "/images/maker-cart/get-started/activity-poster-2.jpg",
    alt: "How to make a cardboard maze activity poster",
  },
];

const posterImages = posters.map((poster) => poster.src);

const openPosterModal = (index = 0) => {
  currentPosterIndex.value = index;
  isPosterModalOpen.value = true;
};

const closePosterModal = () => {
  isPosterModalOpen.value = false;
};

const LAPTOP_ICON_SVG = `<svg width="68" height="51" viewBox="0 0 68 51" fill="none" xmlns="http://www.w3.org/2000/svg">
  <path d="M0.666687 44C0.666687 42.159 2.15907 40.6666 4.00002 40.6666H64C65.841 40.6666 67.3334 42.159 67.3334 44V47.3333C67.3334 49.1742 65.841 50.6666 64 50.6666H4.00002C2.15907 50.6666 0.666687 49.1742 0.666687 47.3333V44Z" fill="#E00069"/>
  <path d="M64 40.6666V40.9049C63.9999 42.6142 62.5077 43.9995 60.667 43.9996H7.33301C5.49228 43.9995 4.00011 42.6142 4 40.9049V40.6666H64ZM20.5664 0.666626C22.133 0.666626 23.4358 1.91968 24.6777 2.87463C25.3986 3.42885 26.324 3.76226 27.333 3.76233H40.667C41.676 3.76226 42.6014 3.42885 43.3223 2.87463C44.5642 1.91968 45.867 0.666626 47.4336 0.666626H60.667C62.5078 0.66679 64 2.05298 64 3.76233V35.6666H4V3.76233C4 2.05298 5.49221 0.666789 7.33301 0.666626H20.5664Z" fill="#E00069"/>
</svg>`;
</script>

<style scoped>
.posters-section {
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
  padding: 40px 0;
}

.posters-header {
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
}

.posters-icon {
  display: flex;
  justify-content: left;
  width: 100%;
  height: 80px;
}

.posters-heading-row {
  display: flex;
  gap: 24px;
  align-items: flex-start;
  justify-content: space-between;
  width: 100%;
}

.posters-title {
  flex: 1;
  margin: 0;
  font-family: var(--font-family-primary);
  font-size: var(--font-size-display-small);
  font-weight: var(--font-weight-bold);
  line-height: var(--line-height-display-small);
  color: var(--blue-dark-2);
}

.posters-intro {
  flex: 0 0 628px;
  max-width: 628px;
  margin: 0;
  text-align: right;
  font-family: var(--font-family-primary);
  font-size: var(--font-size-title-large);
  font-weight: var(--font-weight-medium);
  line-height: var(--line-height-title-large);
  color: var(--black-body-text);
}

.posters-body {
  display: flex;
  flex-direction: column;
  gap: 40px;
  align-items: center;
  width: 100%;
}

.posters-gallery {
  display: flex;
  gap: 40px;
  align-items: flex-start;
  justify-content: center;
  width: 100%;
  padding: 24px;
  background-color: var(--badge-blue-light-1);
  border-radius: 24px;
  box-sizing: border-box;
}

.posters-gallery__item {
  display: block;
  flex: 0 1 333px;
  max-width: 333px;
  padding: 0;
  overflow: hidden;
  background: none;
  border: none;
  border-radius: 10px;
  cursor: pointer;
}

.posters-gallery__item:focus-visible {
  outline: 3px solid var(--primary-blue);
  outline-offset: 4px;
}

.posters-gallery__image {
  display: block;
  width: 100%;
  height: auto;
  aspect-ratio: 333 / 500;
  object-fit: cover;
  border-radius: 10px;
}

@media (max-width: 1200px) {
  .posters-intro {
    flex: 1;
    max-width: none;
  }
}

@media (max-width: 992px) {
  .posters-heading-row {
    flex-direction: column;
    gap: 16px;
  }

  .posters-intro {
    text-align: left;
  }

  .posters-gallery {
    gap: 24px;
  }
}

@media (max-width: 768px) {
  .posters-section {
    gap: 24px;
    padding: 16px 0;
  }

  .posters-icon {
    height: 40px;
  }

  .posters-icon :deep(svg) {
    width: auto;
    height: 40px;
  }

  .posters-heading-row {
    gap: 16px;
  }

  .posters-title {
    font-size: var(--font-size-title-large);
    line-height: var(--line-height-title-large);
  }

  .posters-intro {
    font-size: var(--font-size-title-medium);
    line-height: var(--line-height-title-medium);
    font-weight: var(--font-weight-regular);
    letter-spacing: 0.024px;
  }

  .posters-body {
    gap: 24px;
  }

  .posters-gallery {
    flex-direction: row;
    align-items: flex-start;
    justify-content: center;
    gap: 16px;
    padding: 24px;
  }

  .posters-gallery__item {
    flex: 0 1 134px;
    width: 134px;
    max-width: 134px;
    border-radius: 5px;
  }

  .posters-gallery__image {
    aspect-ratio: 133 / 200;
    border-radius: 5px;
  }

  .posters-body :deep(.action-button--large) {
    min-height: 40px;
    height: 40px;
    padding: 10px 16px;
    border-radius: 8px;
    font-size: var(--font-size-title-small);
    line-height: var(--line-height-title-small);
  }

  .posters-body :deep(.action-button__text) {
    font-size: var(--font-size-title-small);
    line-height: var(--line-height-title-small);
    letter-spacing: 0.014px;
  }

  .posters-body :deep(.action-button--large .action-button__icon svg) {
    width: 20px;
    height: 20px;
  }
}
</style>
